import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Youtube, ArrowUpRight, X, Loader2 } from "lucide-react";
import { fadeUp, staggerContainer, staggerItem } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNEL_URL = "https://www.youtube.com/@rapidsteno";

const VideoCard = ({ v, onPlay }) => (
    <motion.button
        variants={staggerItem}
        onClick={() => onPlay(v)}
        data-testid={`yt-video-${v.videoId}`}
        className="group text-left relative rounded-2xl overflow-hidden glass border border-white/10 hover:border-cyan-400/40 transition focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
    >
        <div className="relative aspect-video overflow-hidden bg-black">
            <img
                src={v.thumbnail}
                alt={v.title}
                loading="lazy"
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            {v.lengthText && (
                <span className="absolute bottom-2 right-2 font-mono text-[10px] bg-black/80 text-white rounded px-1.5 py-0.5">
                    {v.lengthText}
                </span>
            )}
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-red-600/90 text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-mono">
                <Youtube className="h-3 w-3" />
                YouTube
            </span>
            {/* Center play overlay */}
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                <span className="h-16 w-16 rounded-full bg-cyan-400 grid place-items-center shadow-[0_0_30px_-6px_rgba(0,229,255,0.7)]">
                    <Play className="h-6 w-6 fill-black text-black ml-1" />
                </span>
            </span>
        </div>
        <div className="p-5">
            <h3 className="text-white text-[15px] font-semibold leading-snug line-clamp-2 min-h-[44px]">
                {v.title}
            </h3>
            <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500 font-mono">
                {v.viewCountText && <span>{v.viewCountText}</span>}
                {v.viewCountText && v.publishedTimeText && (
                    <span className="text-zinc-700">·</span>
                )}
                {v.publishedTimeText && <span>{v.publishedTimeText}</span>}
            </div>
        </div>
    </motion.button>
);

const YouTubeLibrary = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [active, setActive] = useState(null);

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/youtube/rapidsteno?limit=8`)
            .then((r) => {
                if (!alive) return;
                setVideos(r.data?.videos || []);
            })
            .catch((e) => {
                if (!alive) return;
                setError(e?.message || "Failed to load videos");
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, []);

    // Lock scroll when modal open
    useEffect(() => {
        if (active) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [active]);

    return (
        <section
            id="youtube"
            data-testid="youtube-section"
            className="section py-24 md:py-32 bg-[#070708]/70"
        >
            <motion.div
                className="orb"
                style={{
                    width: 520,
                    height: 520,
                    bottom: "5%",
                    right: "-180px",
                    background:
                        "radial-gradient(circle, rgba(255,0,0,0.16), transparent 60%)",
                }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    {...fadeUp}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
                >
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                            <Youtube className="h-4 w-4" />
                            07 / Video Library
                        </span>
                        <h2
                            data-testid="youtube-heading"
                            className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        >
                            Free lectures from
                            <br />
                            <span className="text-grad-cyan">
                                @rapidsteno on YouTube.
                            </span>
                        </h2>
                        <p className="mt-5 text-zinc-400 text-lg">
                            Stenography, typing speed and Govt-exam strategy — all
                            uploaded fresh from our Prayagraj studio.
                        </p>
                    </div>
                    <a
                        href={CHANNEL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="yt-subscribe"
                        className="self-start inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold px-6 py-3 transition shadow-[0_0_30px_-12px_rgba(255,0,0,0.7)]"
                    >
                        <Youtube className="h-4 w-4" />
                        Subscribe Now
                        <ArrowUpRight className="h-4 w-4" />
                    </a>
                </motion.div>

                {loading && (
                    <div className="grid place-items-center py-20 text-zinc-500">
                        <Loader2 className="h-6 w-6 animate-spin text-cyan-300" />
                        <span className="mt-4 font-mono text-xs uppercase tracking-[0.25em]">
                            Loading latest videos…
                        </span>
                    </div>
                )}

                {error && !loading && (
                    <div className="text-center py-12">
                        <p className="text-zinc-400">
                            Couldn&apos;t load videos right now.{" "}
                            <a
                                href={CHANNEL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-300 underline"
                            >
                                Visit the channel directly.
                            </a>
                        </p>
                    </div>
                )}

                {!loading && !error && videos.length > 0 && (
                    <motion.div
                        variants={staggerContainer}
                        initial="initial"
                        whileInView="whileInView"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
                    >
                        {videos.map((v) => (
                            <VideoCard key={v.videoId} v={v} onPlay={setActive} />
                        ))}
                    </motion.div>
                )}
            </div>

            {/* Player modal */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                        data-testid="yt-modal"
                        className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md grid place-items-center p-4 md:p-10"
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-5xl glass rounded-2xl overflow-hidden border border-white/10"
                        >
                            <button
                                onClick={() => setActive(null)}
                                data-testid="yt-modal-close"
                                aria-label="Close"
                                className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-black/70 backdrop-blur text-white hover:bg-black grid place-items-center"
                            >
                                <X className="h-5 w-5" />
                            </button>
                            <div className="aspect-video bg-black">
                                <iframe
                                    title={active.title}
                                    src={`https://www.youtube-nocookie.com/embed/${active.videoId}?autoplay=1&rel=0&modestbranding=1`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    className="w-full h-full"
                                />
                            </div>
                            <div className="p-5 flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="text-white font-display font-semibold text-lg md:text-xl">
                                        {active.title}
                                    </h3>
                                    <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500 font-mono">
                                        {active.viewCountText && (
                                            <span>{active.viewCountText}</span>
                                        )}
                                        {active.viewCountText &&
                                            active.publishedTimeText && (
                                                <span className="text-zinc-700">·</span>
                                            )}
                                        {active.publishedTimeText && (
                                            <span>{active.publishedTimeText}</span>
                                        )}
                                    </div>
                                </div>
                                <a
                                    href={active.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="yt-watch-youtube"
                                    className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold px-4 py-2.5 transition"
                                >
                                    <Youtube className="h-4 w-4" />
                                    Watch on YouTube
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default YouTubeLibrary;
